/**
 * Voluum metric utilities for SmswiserCampaignStatsTab.
 * For Media Devoted | Created By Ciro Autiero
 */

import { formatCost } from '@/lib/utils';
import type { ColumnDef } from '../constants';
import { VOLUUM_METRIC_ALIASES } from '../metric-aliases';

/** Row shape for aggregation – voluum or metrics (API returns metrics for phones). */
type VoluumRow = { voluum?: Record<string, number> | null; metrics?: Record<string, number> };

const ADDITIVE_VOLUUM_KEYS = [
  'impressions',
  'sends',
  'visits',
  'uniqueVisits',
  'clicks',
  'uniqueClicks',
  'conversions',
  'customConversions6',
  'customConversions7',
  'revenue',
  'cost',
  'profit',
] as const;

function safeNum(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function safeDivide(num: number, denom: number): number {
  return denom && Number.isFinite(denom) ? num / denom : 0;
}

export function getVoluumValue(
  voluum: Record<string, number> | null | undefined,
  key: string,
): number | null | undefined {
  if (!voluum) return undefined;
  const aliases = VOLUUM_METRIC_ALIASES[key] ?? [key];
  for (const k of aliases) {
    const v = voluum[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  const direct = voluum[key];
  if (typeof direct === 'number' && Number.isFinite(direct)) return direct;
  return undefined;
}

function computeDerivedMetric(
  voluum: Record<string, number>,
  key: string,
): number | undefined {
  const safe = (k: string) => (typeof voluum[k] === 'number' && Number.isFinite(voluum[k]) ? voluum[k]! : 0);
  const n = (canonical: string) => getVoluumValue(voluum, canonical) ?? 0;
  const imp = n('impressions');
  const uniqVisits = n('uniqueVisits');
  const uniqClicks = n('uniqueClicks') || safe('clicks');
  const reg = n('customConversions6');
  const ftd = n('customConversions7');
  const costVal = safe('cost');
  const profitVal = safe('profit');
  const divide = (a: number, b: number) => (b && Number.isFinite(b) ? a / b : 0);
  switch (key) {
    case 'ImCTR':
      return imp > 0 ? divide(uniqClicks, imp) * 100 : undefined;
    case 'uCTR':
      return uniqVisits > 0 ? divide(uniqClicks, uniqVisits) * 100 : undefined;
    case 'Click2Reg':
      return uniqClicks > 0 ? divide(reg, uniqClicks) * 100 : undefined;
    case 'Reg2Dep':
      return reg > 0 ? divide(ftd, reg) * 100 : undefined;
    case 'roi':
      return costVal > 0 ? divide(profitVal, costVal) * 100 : undefined;
    case 'uEPC':
      return uniqClicks > 0 ? divide(safe('revenue'), uniqClicks) : undefined;
    case 'uEPV':
      return uniqVisits > 0 ? divide(safe('revenue'), uniqVisits) : undefined;
    case 'epv':
      return (safe('visits') || imp) > 0 ? divide(safe('revenue'), safe('visits') || imp) : undefined;
    case 'epc':
      return (safe('clicks') || uniqClicks) > 0
        ? divide(safe('revenue'), safe('clicks') || uniqClicks)
        : undefined;
    case 'epl': {
      const conv = n('conversions');
      return conv > 0 ? divide(safe('revenue'), conv) : undefined;
    }
    case 'CPS':
      return imp > 0 ? divide(costVal, imp) : undefined;
    case 'CPuC':
      return uniqClicks > 0 ? divide(costVal, uniqClicks) : undefined;
    case 'CPL':
      return reg > 0 ? divide(costVal, reg) : undefined;
    default:
      return undefined;
  }
}

export function getVoluumValueForDisplay(
  voluum: Record<string, number> | null | undefined,
  key: string,
): number | null | undefined {
  const v = getVoluumValue(voluum, key);
  if (v != null && Number.isFinite(v)) return v;
  if (!voluum) return undefined;
  return computeDerivedMetric(voluum, key);
}

function normalizePercentForDisplay(value: number): number {
  return value;
}

export function formatMetric(
  value: number | null | undefined,
  format: string,
  currencyCode = 'USD',
): string {
  if (value == null || !Number.isFinite(value)) return '—';
  switch (format) {
    case 'int':
      return Math.round(value).toLocaleString();
    case 'currency':
      return formatCost(value, currencyCode) || '—';
    case 'percent':
      return `${normalizePercentForDisplay(value).toFixed(2)}%`;
    case 'decimal':
      return value.toFixed(2);
    default:
      return String(value);
  }
}

export function formatMetricValue(
  v: number | null | undefined,
  col: ColumnDef,
  currencyCode = 'USD',
): string {
  return formatMetric(v, col.format, currencyCode);
}

export function aggregateVoluum(rows: VoluumRow[]): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const r of rows) {
    const m = r.voluum ?? r.metrics;
    if (!m) continue;
    for (const k of ADDITIVE_VOLUUM_KEYS) {
      const v = getVoluumValue(m, k);
      if (typeof v === 'number' && Number.isFinite(v)) {
        acc[k] = (acc[k] ?? 0) + v;
      }
    }
  }

  const imp = safeNum(acc.impressions) || safeNum(acc.sends);
  const uniqVisits = safeNum(acc.uniqueVisits) || safeNum(acc.visits);
  const uniqClicks = safeNum(acc.uniqueClicks) || safeNum(acc.clicks);
  const reg = safeNum(acc.customConversions6);
  const ftd = safeNum(acc.customConversions7);
  const rev = safeNum(acc.revenue);
  const costVal = safeNum(acc.cost);
  const profitVal = safeNum(acc.profit);
  const visits = safeNum(acc.visits) || imp;
  const clicks = safeNum(acc.clicks) || uniqClicks;

  if (imp > 0) acc.ImCTR = safeDivide(uniqClicks, imp) * 100;
  if (uniqVisits > 0) acc.uCTR = safeDivide(uniqClicks, uniqVisits) * 100;
  if (uniqClicks > 0) acc.Click2Reg = safeDivide(reg, uniqClicks) * 100;
  if (reg > 0) acc.Reg2Dep = safeDivide(ftd, reg) * 100;
  if (costVal > 0) acc.roi = safeDivide(profitVal, costVal) * 100;
  if (uniqClicks > 0) acc.uEPC = safeDivide(rev, uniqClicks);
  if (uniqVisits > 0) acc.uEPV = safeDivide(rev, uniqVisits);
  if (visits > 0) acc.epv = safeDivide(rev, visits);
  if (clicks > 0) acc.epc = safeDivide(rev, clicks);
  const conv = safeNum(acc.conversions);
  if (conv > 0) acc.epl = safeDivide(rev, conv);
  if (imp > 0) acc.CPS = safeDivide(costVal, imp);
  if (uniqClicks > 0) acc.CPuC = safeDivide(costVal, uniqClicks);
  if (reg > 0) acc.CPL = safeDivide(costVal, reg);

  return acc;
}
